/**
 * ImageButton.tsx — Clickable image button that delegates rendering to
 * `CirclerImageButton` and adds click tracking.
 *
 * JSON trigger: `uiCompType.method: "renderImageButton"`
 *
 * **Behaviour:**
 *  - Wraps `CirclerImageButton` with an `onClick` handler.
 *  - Navigates to `sectionData.link` on click.
 *  - Calls `trackItem` with `sectionData.trackInfo` for analytics.
 *  - Supports `options.newTab` for external links.
 *
 * **Style slots:** Inherited from `CirclerImageButton`.
 */
import React from "react";
import { SectionData, ComponentRenderProps } from "./index";
import CirclerImageButton from "./CirclerImageButton";
import { trackItem } from "@/utils";

interface ImageButtonProps {
    sectionData: SectionData;
    props: ComponentRenderProps;
    index?: number;
}

const ImageButton = ({ sectionData, props, index = 1 }: ImageButtonProps): JSX.Element | null => {
    const newTab = sectionData.newTab || sectionData.uiCompType?.options?.newTab;
    return sectionData.link
        ? <a key={`${sectionData.trackId ?? 'imageButton_'}${index}`} href={sectionData.link}
            id={sectionData.trackId || 'imageButton_'}
            target={newTab ? "_blank" : "_self"}
            onClick={(e) => {
                trackItem(sectionData.trackInfo || { event: 'imageButtonClick', section: sectionData.trackId });
            }}
        >
            <CirclerImageButton sectionData={sectionData} props={props} index={index} />
        </a>
        : <CirclerImageButton sectionData={sectionData} props={props} index={index} />;
};

export default ImageButton;
